export type BioCameraQualityStatus =
  | 'DOCUMENT_NOT_DETECTED'
  | 'LOW_LIGHT'
  | 'TOO_FAR'
  | 'TOO_CLOSE'
  | 'OFF_CENTER'
  | 'READY';

export interface BioCameraRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface BioCameraQualityConfig {
  lowLightLuminance: number;
  minCandidateAreaRatio: number;
  minDocumentAreaRatio: number;
  maxDocumentAreaRatio: number;
  maxCenterOffsetRatio: number;
  minAspectRatio: number;
  maxAspectRatio: number;
  minRectangularity: number;
  edgeThreshold: number;
  sampleStep: number;
}

export interface BioCameraQualityResult {
  documentDetected: boolean;
  status: BioCameraQualityStatus;
  luminance: number;
  documentAreaRatio: number;
  boundingBox: BioCameraRect | null;
  aspectRatio: number;
  rectangularity: number;
  centerX: number;
  centerY: number;
  centerOffsetX: number;
  centerOffsetY: number;
}

export const BIO_CAMERA_QUALITY_CONFIG: BioCameraQualityConfig = {
  lowLightLuminance: 58,
  minCandidateAreaRatio: 0.08,
  minDocumentAreaRatio: 0.45,
  maxDocumentAreaRatio: 1.15,
  maxCenterOffsetRatio: 0.18,
  minAspectRatio: 1.2,
  maxAspectRatio: 2.2,
  minRectangularity: 0.18,
  edgeThreshold: 28,
  sampleStep: 4,
};

export function analyzeBioCameraFrame(
  imageData: ImageData,
  config: BioCameraQualityConfig = BIO_CAMERA_QUALITY_CONFIG,
  guideRect: BioCameraRect = { x: 0, y: 0, width: imageData.width, height: imageData.height },
): BioCameraQualityResult {
  const { width, height, data } = imageData;
  const sampledWidth = Math.ceil(width / config.sampleStep);
  const sampledHeight = Math.ceil(height / config.sampleStep);
  const luminance = new Float32Array(sampledWidth * sampledHeight);
  let luminanceSum = 0;

  for (let sampleY = 0; sampleY < sampledHeight; sampleY += 1) {
    for (let sampleX = 0; sampleX < sampledWidth; sampleX += 1) {
      const x = Math.min(sampleX * config.sampleStep, width - 1);
      const y = Math.min(sampleY * config.sampleStep, height - 1);
      const index = (y * width + x) * 4;
      const value = 0.2126 * data[index] + 0.7152 * data[index + 1] + 0.0722 * data[index + 2];
      luminance[sampleY * sampledWidth + sampleX] = value;
      luminanceSum += value;
    }
  }

  const edge = new Uint8Array(sampledWidth * sampledHeight);
  for (let y = 0; y < sampledHeight; y += 1) {
    for (let x = 0; x < sampledWidth; x += 1) {
      const current = luminance[y * sampledWidth + x];
      const right = luminance[y * sampledWidth + Math.min(x + 1, sampledWidth - 1)];
      const down = luminance[Math.min(y + 1, sampledHeight - 1) * sampledWidth + x];
      edge[y * sampledWidth + x] = Math.max(Math.abs(current - right), Math.abs(current - down)) >= config.edgeThreshold ? 1 : 0;
    }
  }

  const candidates: Array<{ bounds: BioCameraRect; edgeCount: number }> = [];
  const visited = new Uint8Array(edge.length);
  for (let start = 0; start < edge.length; start += 1) {
    if (!edge[start] || visited[start]) {
      continue;
    }
    const queue = [start];
    visited[start] = 1;
    let minX = sampledWidth;
    let minY = sampledHeight;
    let maxX = -1;
    let maxY = -1;
    let edgeCount = 0;
    while (queue.length) {
      const current = queue.pop() as number;
      const x = current % sampledWidth;
      const y = Math.floor(current / sampledWidth);
      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x);
      maxY = Math.max(maxY, y);
      edgeCount += 1;
      for (let offsetY = -1; offsetY <= 1; offsetY += 1) {
        for (let offsetX = -1; offsetX <= 1; offsetX += 1) {
          const nextX = x + offsetX;
          const nextY = y + offsetY;
          if (nextX < 0 || nextY < 0 || nextX >= sampledWidth || nextY >= sampledHeight) {
            continue;
          }
          const next = nextY * sampledWidth + nextX;
          if (edge[next] && !visited[next]) {
            visited[next] = 1;
            queue.push(next);
          }
        }
      }
    }
    if (maxX > minX && maxY > minY) {
      candidates.push({
        bounds: {
          x: minX * config.sampleStep,
          y: minY * config.sampleStep,
          width: (maxX - minX + 1) * config.sampleStep,
          height: (maxY - minY + 1) * config.sampleStep,
        },
        edgeCount,
      });
    }
  }

  const candidate = candidates
    .map((item) => {
      const aspectRatio = item.bounds.width / item.bounds.height;
      const area = item.bounds.width * item.bounds.height;
      const perimeter = 2 * (item.bounds.width + item.bounds.height);
      return { ...item, aspectRatio, area, rectangularity: item.edgeCount / Math.max(perimeter / config.sampleStep, 1) };
    })
    .filter((item) => item.aspectRatio >= config.minAspectRatio && item.aspectRatio <= config.maxAspectRatio)
    .sort((first, second) => second.area - first.area)[0];

  const averageLuminance = luminanceSum / luminance.length;
  const boundingBox = candidate?.bounds || null;
  const aspectRatio = candidate?.aspectRatio || 0;
  const rectangularity = candidate?.rectangularity || 0;
  const documentDetected = !!candidate &&
    candidate.area / (width * height) >= config.minCandidateAreaRatio &&
    rectangularity >= config.minRectangularity;
  const documentCenterX = boundingBox ? (boundingBox.x + boundingBox.width / 2) / width : 0.5;
  const documentCenterY = boundingBox ? (boundingBox.y + boundingBox.height / 2) / height : 0.5;
  const guideCenterX = (guideRect.x + guideRect.width / 2) / width;
  const guideCenterY = (guideRect.y + guideRect.height / 2) / height;
  const centerOffsetX = Math.abs(documentCenterX - guideCenterX) / Math.max(guideRect.width / width, 0.01);
  const centerOffsetY = Math.abs(documentCenterY - guideCenterY) / Math.max(guideRect.height / height, 0.01);
  const documentAreaRatio = boundingBox ? (boundingBox.width * boundingBox.height) / (guideRect.width * guideRect.height) : 0;

  let status: BioCameraQualityStatus = 'DOCUMENT_NOT_DETECTED';
  if (averageLuminance < config.lowLightLuminance) {
    status = 'LOW_LIGHT';
  } else if (documentDetected && (centerOffsetX > config.maxCenterOffsetRatio || centerOffsetY > config.maxCenterOffsetRatio)) {
    status = 'OFF_CENTER';
  } else if (documentDetected && documentAreaRatio < config.minDocumentAreaRatio) {
    status = 'TOO_FAR';
  } else if (documentDetected && documentAreaRatio > config.maxDocumentAreaRatio) {
    status = 'TOO_CLOSE';
  } else if (documentDetected) {
    status = 'READY';
  }

  return {
    documentDetected,
    status,
    luminance: averageLuminance,
    documentAreaRatio,
    boundingBox,
    aspectRatio,
    rectangularity,
    centerX: documentCenterX,
    centerY: documentCenterY,
    centerOffsetX,
    centerOffsetY,
  };
}
